/**
 * useStoreNonReactive
 *
 * Hook for accessing store state and persist without subscribing to changes.
 * Use this when you only need write access or refs, not reactive reads.
 *
 * @example
 * const { persist } = useStoreNonReactive('rows');
 * if (persist.persistType === 'collection') {
 *   await persist.add({ cells: {} });
 * }
 */

import { useContext } from 'react';
import { StoresContext } from './core/StoresContext';
import { getStoreFromContext } from './core/getStoreFromContext';
import type { DocOrCollectionState, DocOrCollectionPersist } from './Store.types';

/**
 * Access store state and persist without subscribing to changes.
 * No reactivity, does NOT cause re-renders on state changes.
 *
 * @param storeName - Name of the store
 */
export function useStoreNonReactive(
  storeName: string
): { state: DocOrCollectionState; persist: DocOrCollectionPersist } {
  const ctx = useContext(StoresContext);
  return getStoreFromContext(ctx, storeName, 'any');
}

